import { URLS } from '@constants'
import {
  GETField,
  GETFieldById,
  GETUndefinedFields,
  PayloadCreateField,
  POSTCreateAllFields,
  POSTDefinedFields,
  POSTOrderFieldType,
  POSTTitleFieldType,
  POSTVisibilityFieldType,
} from '@types'

import { commonAPI } from './common.api'

export const fieldAPI = commonAPI.injectEndpoints({
  endpoints: build => ({
    fetchField: build.mutation<GETFieldById, number | string | void>({
      query: id => ({
        url: URLS.FRONT_CFG + `/field/${id}`,
      }),
    }),
    createField: build.mutation<GETField, PayloadCreateField>({
      query: column => ({
        url: URLS.FRONT_CFG + '/field',
        method: 'POST',
        body: { ...column, id: undefined },
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields'],
    }),
    createAllFields: build.mutation<void, POSTCreateAllFields>({
      query: allFieldsParams => ({
        url: URLS.FRONT_CFG + '/field/addAll',
        method: 'POST',
        body: { ...allFieldsParams, onlyRequired: false },
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields'],
    }),
    deleteAllFields: build.mutation<void, POSTCreateAllFields>({
      query: allFieldsParams => ({
        url: URLS.FRONT_CFG + '/field/all',
        method: 'DELETE',
        body: { ...allFieldsParams, onlyRequired: false },
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields'],
    }),
    createMandatoryFields: build.mutation<void, POSTCreateAllFields>({
      query: allFieldsParams => ({
        url: URLS.FRONT_CFG + '/field/addAll',
        method: 'POST',
        body: { ...allFieldsParams, onlyRequired: true },
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields'],
    }),
    updateField: build.mutation<GETField, PayloadCreateField>({
      query: column => ({
        url: URLS.FRONT_CFG + '/field',
        method: 'POST',
        body: column,
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields', 'EnrichedContainer'],
    }),
    updateFieldTitle: build.mutation<void, POSTTitleFieldType>({
      query: column => ({
        url: URLS.FRONT_CFG + '/field/title',
        method: 'POST',
        body: column,
      }),
      invalidatesTags: ['SingleForm'],
    }),
    deleteField: build.mutation<void, number | string>({
      query: id => ({
        url: URLS.FRONT_CFG + `/field/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SingleForm', 'UndefinedFields', 'DefinedFields'],
    }),
    updateOrderFields: build.mutation<void, POSTOrderFieldType[]>({
      query: fields => ({
        url: URLS.FRONT_CFG + '/field/order',
        method: 'POST',
        body: fields,
      }),
      invalidatesTags: ['SingleForm'],
    }),
    updateVisibilityFieldById: build.mutation<void, POSTVisibilityFieldType>({
      query: field => ({
        url: URLS.FRONT_CFG + '/field/visibility',
        method: 'POST',
        body: field,
      }),
      invalidatesTags: ['SingleForm', 'EnrichedContainer'],
    }),
    fetchUndefinedFieldsByFormCode: build.query<
      GETUndefinedFields,
      { formCode: string; filter?: string }
    >({
      query: ({ formCode, filter }) => ({
        url: URLS.FRONT_CFG + `/field/formCode/${formCode}/undefined`,
        params: { filter },
      }),
      providesTags: ['UndefinedFields'],
    }),
    updateDefinedFieldsByFormCode: build.mutation<void, POSTDefinedFields>({
      query: ({ formCode, fields }) => ({
        url: URLS.FRONT_CFG + `/field/formCode/${formCode}/defined`,
        method: 'POST',
        body: fields,
      }),
      invalidatesTags: ['DefinedFields', 'UndefinedFields'],
    }),
  }),
})

export const {
  useCreateFieldMutation,
  useCreateAllFieldsMutation,
  useDeleteAllFieldsMutation,
  useCreateMandatoryFieldsMutation,
  useDeleteFieldMutation,
  useFetchFieldMutation,
  useUpdateFieldMutation,
  useUpdateFieldTitleMutation,
  useUpdateOrderFieldsMutation,
  useUpdateVisibilityFieldByIdMutation,
  useFetchUndefinedFieldsByFormCodeQuery,
  useUpdateDefinedFieldsByFormCodeMutation,
} = fieldAPI
